import type { AxiosInstance } from 'axios';
import type { paths } from '../schema';
import type { TimelineEntry } from '../types/timeline';
import type {
  PaginatedResponse,
  RequestOptions,
  RequestOptionsWithParams,
} from './types';
import { curry1 } from './utils';

export type RoleTimelineEntry = TimelineEntry<
  Record<string, any>,
  | 'role_created'
  | 'role_name_updated'
  | 'role_permission_groups_updated'
  | 'role_deleted'
>;

export type RoleHistoryParams = Required<
  paths['/api/role/{role_id}/history']['get']['parameters']
>['query'];

export interface RoleHistory
  extends Omit<
    paths['/api/role/{role_id}/history']['get']['responses']['200']['content']['application/json'],
    'results'
  > {
  results: RoleTimelineEntry[];
}

export type Role = Required<
  Mutable<
    paths['/api/role']['get']['responses']['200']['content']['application/json']['results'][number]
  >
>;

type RoleParams = Exclude<
  paths['/api/role']['get']['parameters']['query'],
  undefined
>;

/**
 * The backend API returns a paginated response only when the page_size parameter is present.
 * The generic 'paginated' argument forces the type of page_size to be required. This is needed when
 * the params argument is not inlined in the function call for the api and query utilities to make the
 * return type inference in {@link GetRolesResponse} will give you the correct result
 */
export type GetRolesParams<T extends 'paginated' | undefined = undefined> =
  T extends 'paginated'
    ? Pretty<Omit<RoleParams, 'page_size'> & { page_size: number }>
    : RoleParams;

export type GetRolesResponse<
  TParams extends GetRolesParams | undefined = undefined,
> = TParams extends { page_size: number } ? PaginatedResponse<Role> : Role[];

export const createRole = async (
  instance: AxiosInstance,
  body: paths['/api/role']['post']['requestBody']['content']['application/json'],
  options: RequestOptions = {}
): Promise<Role> => {
  const res = await instance.post('/role', body, options);

  return res.data;
};

export const getRoleHistory = async (
  instance: AxiosInstance,
  id: string,
  options?: RequestOptionsWithParams<RoleHistoryParams>
): Promise<RoleHistory> => {
  const res = await instance.get<RoleHistory>(`/role/${id}/history`, options);

  return res.data;
};

export const getRoles = async <
  TParams extends GetRolesParams | undefined = undefined,
>(
  instance: AxiosInstance,
  options: RequestOptionsWithParams<TParams> = {}
): Promise<GetRolesResponse<TParams>> => {
  const res = await instance.get<GetRolesResponse<TParams>>('/role', options);

  return res.data;
};

export class RoleService {
  public createRole;
  public getRoleHistory;
  public getRoles;

  constructor(private instance: AxiosInstance) {
    this.createRole = curry1(createRole)(this.instance);
    this.getRoleHistory = curry1(getRoleHistory)(this.instance);
    this.getRoles = curry1(getRoles)(this.instance);
  }
}
